import { Principal } from '@icp-sdk/core/principal'
import { logger } from '@plug-core/logger'
import { base64ToArrayBuffer } from '@plug-core/utils/arrayBuffer'
import { Banner, Tabs, TabsContent, TabsList, TabsTrigger, Text } from '@ui'
import { decodeCallArgs } from '@utils/decodeArgs/decodeCallArgs'
import { getCandid } from '@utils/decodeArgs/downloadCandid'
import { fetchCanisterConsentMessage } from '@utils/fetchCanisterConsentMessage'
import React, { FC, useEffect, useState } from 'react'
import { CodeBlock, dracula } from 'react-code-blocks'
import Markdown from 'react-markdown'

interface CanisterCallProps {
  canisterId: string
  method: string
  arg: string
}

const CanisterCall: FC<CanisterCallProps> = ({ canisterId, method, arg }) => {
  const [decodedArguments, setDecodedArguments] = useState<string>()
  const [currentTab, setCurrentTab] = useState(0)
  const [noConsent, setNoConsent] = useState(false)
  const [consentMessage, setConsentMessage] = useState<string>()

  const handleChange = (newValue: string) => {
    setCurrentTab(Number(newValue))
  }

  useEffect(() => {
    getCandid(Principal.from(canisterId))
      .then((candid) => {
        return decodeCallArgs(method, arg, candid)
      })
      .then(setDecodedArguments)
      .catch((e) => {
        logger.error('Failed to decode arguments', { originalError: e })
      })
  }, [canisterId])

  useEffect(() => {
    fetchCanisterConsentMessage(canisterId, method, base64ToArrayBuffer(arg))
      .then(setConsentMessage)
      .catch(() => {
        setNoConsent(true)
      })
  }, [canisterId, method, arg])

  const renderPropertyRow = (title: string, value: string) => (
    <div className="flex flex-col gap-2">
      <Text variant="body-s" tone="fg-2">
        {title}
      </Text>
      <div className="rounded-sm bg-surface px-4 py-2">
        <Text variant="mono" tone="fg" breakAll>
          {value}
        </Text>
      </div>
    </div>
  )

  return (
    <Tabs value={String(currentTab)} onValueChange={handleChange} className="m-4">
      <TabsList className="self-stretch">
        <TabsTrigger value="0" className="flex-1">
          Info
        </TabsTrigger>
        <TabsTrigger value="1" className="flex-1">
          Parameters
        </TabsTrigger>
      </TabsList>
      <TabsContent value="0" className="pb-4">
        <div className="flex flex-col gap-2">
          {noConsent && (
            <Banner tone="warning">
              Canister did not provide consent message - use at own risk
            </Banner>
          )}
          {consentMessage && (
            <div
              className={
                'flex flex-col rounded-lg bg-surface p-4 font-text text-fg ' +
                '[&_p]:my-0.5 [&_p]:text-[16px] [&_p]:font-medium ' +
                '[&_blockquote]:m-2.5 [&_blockquote]:border-l-2 [&_blockquote]:border-hairline-strong [&_blockquote]:pl-1.5'
              }
            >
              <Markdown>{consentMessage}</Markdown>
            </div>
          )}
          {renderPropertyRow('Canister ID', canisterId)}
          {renderPropertyRow('Method', method)}
        </div>
      </TabsContent>
      <TabsContent value="1" className="pb-4">
        <div className="flex flex-col">
          <Text variant="body-s" tone="fg-2" className="mb-2">
            Parameters
          </Text>
          <div className="overflow-hidden rounded-md font-mono">
            <CodeBlock
              text={decodedArguments}
              language="perl"
              theme={dracula}
              showLineNumbers={false}
              codeContainerStyle={{ height: 150 }}
            />
          </div>
        </div>
      </TabsContent>
    </Tabs>
  )
}

export default CanisterCall
